import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { zodDeliveryNoteResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { useInitDlns } from '~/models/deliveries/DeliveryNote/utils/hooks';

import { log } from '~/utils/logging';

import { queryKeysDeliveryNote } from './queryKeys';
import { type DeliveryNoteListItem } from './types';

/**
 * Fetches delivery note data from the API and optionally initializes it.
 * @param {UUID} deliveryNoteId - The ID of the delivery note to fetch
 * @returns {Promise<DeliveryNoteListItem|undefined>} The delivery note data if successful
 * @throws {Error} If the API request fails
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/delivery_note_asset_delivery_note_get
 */
export const fetchDeliveryNote = async (deliveryNoteId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET(deliveryNoteId))
    .json<DeliveryNoteListItem>();

  // Validate the returned data and log any validation errors
  const validatedResponse = zodDeliveryNoteResponseSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Delivery note API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};

/**
 * React Query based custom hook for getting the data for a delivery note with a given ID.
 * @param {UUID} deliveryNoteId - The ID of the delivery note to fetch.
 * @param {Object} options - Additional options for the useQuery hook.
 * @returns {UseQueryResult<DeliveryNoteListItem|undefined>} The result of the useQuery hook.
 */
export const useQueryDeliveryNote = (
  deliveryNoteId: UUID,
  options?: Record<string, unknown>,
): UseQueryResult<DeliveryNoteListItem | undefined> => {
  const initDlns = useInitDlns();

  return useQuery({
    enabled: Boolean(deliveryNoteId),
    async queryFn() {
      const dln = await fetchDeliveryNote(deliveryNoteId);
      const initializedAssets = await initDlns([dln]);

      const initializedDln = initializedAssets[0];

      return initializedDln;
    },
    queryKey: queryKeysDeliveryNote.get(deliveryNoteId),
    ...options,
    refetchOnWindowFocus: false,
  });
};
